import { Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';

export interface ErrorData {
    badRequest?: boolean;
    unauthorized?: boolean;
    notFound?: boolean;
    serverError?: boolean;
    internalError?: boolean;
    validationError?: boolean;
    validationErrors?: ValidationErrors;
}

export class ActionError extends Error {
    __proto__: Error;
    errorData?: ErrorData;

    constructor(message?: string, errorData?: ErrorData) {
        const trueProto = new.target.prototype;
        super(message);
        this.errorData = errorData;
        // Alternatively use Object.setPrototypeOf if you have an ES6 environment.
        this.__proto__ = trueProto;

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }
    }
}

export declare type ValidationErrors = { [index: string]: string | string[] };


export class Action<TIn = {}, TOut = {}> {
    private onSuccessFunc?: (data: TOut) => void;
    private onValidationFailedFunc?: (errors: ValidationErrors) => void;
    private onErrorFunc?: (error: ActionError) => void;
    private execute$?: Subject<TOut>;

    private executeFunc: (data?: TIn) => Observable<TOut>;

    constructor(execFunc: (data?: TIn) => Observable<TOut>) {
        this.executeFunc = execFunc;
    }

    getExecute$ = (): Observable<TOut> => {
        if (this.execute$ === undefined) {
            this.execute$ = new Subject();
        }
        return this.execute$;
    }

    onSuccess = (fc: (data: TOut) => void) => {
        this.onSuccessFunc = fc;
        return this;
    }

    onValidationFailed = (fc: (errors: ValidationErrors) => void) => {
        this.onValidationFailedFunc = fc;
        return this;
    }

    onError = (fc: (error: ActionError) => void) => {
        this.onErrorFunc = fc;
        return this;
    }

    execute = (data?: TIn): void => {

        this.executeFunc(data).pipe(
            first()
        ).subscribe(x => {
            this.execute$.next(x);
        }, e => {
            this.execute$.error(e);
            this.execute$ = undefined;
        });
    }

    handleSuccess = (data: TOut) => {
        if (this.onSuccessFunc) {
            this.onSuccessFunc(data);
        }
    }

    handleError = (error: Error | any) => {
        let actionError: ActionError;

        if (error instanceof ActionError === false) {
            const errorMessage = typeof error === 'string' ? error as string : error instanceof Error ? error.message : "Internal error!";
            actionError = new ActionError(errorMessage);
        } else {
            actionError = error as ActionError;

            const { errorData } = actionError;

            if (errorData && errorData.validationError && errorData.validationErrors && this.onValidationFailedFunc) {
                this.onValidationFailedFunc(errorData.validationErrors);
                return;
            }

        }
        if (this.onErrorFunc) {
            this.onErrorFunc(actionError);
        }
    }
}